import React, { useState } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff, Lock, Mail, User, Moon, Sun } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function SignUpPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  function validate() {
    if (!name) return "نام را وارد کنید.";
    if (!email) return "ایمیل را وارد کنید.";
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(email)) return "ایمیل نامعتبر است.";
    if (!password) return "رمز عبور را وارد کنید.";
    if (password.length < 6) return "رمز عبور باید حداقل ۶ کاراکتر باشد.";
    return "";
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) return setError(v);

    setLoading(true);
    try {
      const res = await axios.post("https://financial.deepmine.ir/s2/signup", {
        name,
        email,
        password,
      });
      alert(res.data.message || "ثبت‌نام موفقیت‌آمیز بود!");
      navigate("/");
    } catch (err) {
      if (err.response) {
        setError(err.response.data.message || "خطایی رخ داده است.");
      } else {
        setError("خطای شبکه، دوباره تلاش کنید.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 transition-colors duration-500 ${
        darkMode
          ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
          : "bg-gradient-to-br from-blue-100 via-blue-200 to-blue-300"
      }`}
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`w-full max-w-md rounded-2xl p-8 shadow-xl border transition-colors duration-500 ${
          darkMode
            ? "bg-slate-800/80 border-slate-700 text-white"
            : "bg-white/80 border-white/50 text-slate-900"
        } backdrop-blur-md`}
      >
        {/* Dark Mode Toggle */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setDarkMode((prev) => !prev)}
            className={`p-2 rounded-full transition-colors duration-300 ${
              darkMode
                ? "bg-slate-700 hover:bg-slate-600"
                : "bg-slate-200 hover:bg-slate-300"
            }`}
          >
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold">ثبت‌نام</h1>
          <p
            className={`mt-1 text-sm ${
              darkMode ? "text-slate-300" : "text-slate-600"
            }`}
          >
            یک حساب کاربری جدید بسازید
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`text-sm border p-3 rounded-md ${
                darkMode
                  ? "text-red-300 bg-red-900/30 border-red-800"
                  : "text-red-600 bg-red-50 border-red-100"
              }`}
            >
              {error}
            </motion.div>
          )}

          {/* Name */}
          <label className="block">
            <span
              className={`text-sm flex items-center gap-2 font-medium ${
                darkMode ? "text-slate-200" : "text-sky-900"
              }`}
            >
              <User size={16} /> نام
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`mt-1 block w-full rounded-lg border px-3 py-2 shadow-sm focus:outline-none focus:ring-2 transition ${
                darkMode
                  ? "bg-slate-700 border-slate-600 text-white focus:ring-slate-500"
                  : "bg-white border-slate-300 text-slate-900 focus:ring-sky-300"
              }`}
              placeholder="نام و نام خانوادگی"
            />
          </label>

          {/* Email */}
          <label className="block">
            <span
              className={`text-sm flex items-center gap-2 font-medium ${
                darkMode ? "text-slate-200" : "text-sky-900"
              }`}
            >
              <Mail size={16} /> ایمیل
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`mt-1 block w-full rounded-lg border px-3 py-2 shadow-sm focus:outline-none focus:ring-2 transition ${
                darkMode
                  ? "bg-slate-700 border-slate-600 text-white focus:ring-slate-500"
                  : "bg-white border-slate-300 text-slate-900 focus:ring-sky-300"
              }`}
              placeholder="you@example.com"
            />
          </label>

          {/* Password */}
          <label className="block relative">
            <span
              className={`text-sm flex items-center gap-2 font-medium ${
                darkMode ? "text-slate-200" : "text-sky-900"
              }`}
            >
              <Lock size={16} /> رمز عبور
            </span>
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`mt-1 block w-full rounded-lg border px-3 py-2 pr-10 shadow-sm focus:outline-none focus:ring-2 transition ${
                darkMode
                  ? "bg-slate-700 border-slate-600 text-white focus:ring-slate-500"
                  : "bg-white border-slate-300 text-slate-900 focus:ring-sky-300"
              }`}
              placeholder="حداقل ۶ کاراکتر"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className={`absolute right-3 top-9 p-1 rounded-md transition-colors ${
                darkMode ? "hover:bg-slate-600" : "hover:bg-slate-100"
              }`}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </label>

          {/* Submit */}
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={loading}
            className={`w-full rounded-xl py-2 font-medium shadow-md disabled:opacity-60 transition-colors ${
              darkMode
                ? "bg-slate-600 hover:bg-slate-500 text-white"
                : "bg-sky-600 hover:bg-sky-700 text-white"
            }`}
          >
            {loading ? "در حال ثبت‌نام..." : "ثبت‌نام"}
          </motion.button>
        </form>

        {/* Footer */}
        <div
          className={`mt-6 text-center text-sm ${
            darkMode ? "text-slate-300" : "text-slate-500"
          }`}
        >
          حساب دارید؟{" "}
          <a
            href="/"
            className={`font-medium ${
              darkMode
                ? "text-sky-400 hover:underline"
                : "text-sky-700 hover:underline"
            }`}
          >
            وارد شوید
          </a>
        </div>
      </motion.div>
    </div>
  );
}
